/**
 * Whether a member has a Connect Group on record.
 *
 * The backend resolves `cgName` from the member's primary `MemberCgZone` row
 * (see `orderByEffectivePrimaryCg` in hogc-one-api), which yields `undefined`
 * for a member with no resolvable CG. But the field also arrives as the literal
 * string "None" for some records — the placeholder the profile UI shows for an
 * unset CG, which has made its way into stored data. Both mean "no CG", so a
 * plain truthiness check silently treats "None" as a real Connect Group.
 *
 * Zone, service and demography all resolve through the CG relation server-side,
 * so a member failing this check has no zone or demography either.
 */
const UNSET_CG_NAMES = new Set(['none', 'n/a', 'nil', '-']);

/**
 * The `cg` id that means "no Connect Group".
 *
 * `cg` holds a real, active row named "None" whose id is 0 — what the admin tool
 * writes when staff remove a member's CG. It is therefore in the directory this
 * module validates against, so a typed name has to be judged by the id it
 * resolves to, not by the name. The id is what a removal writes; the name is a
 * label staff can change, and matching on it would refuse a CG legitimately
 * called something in that family. Mirrors UNSET_CG_ID in hogc-one-api's
 * `src/utils/course-access.ts`.
 *
 * `hasLinkedCg` above still has to go by name: it reads the profile's `cgName`,
 * where no id is available.
 */
export const UNSET_CG_ID = 0;

/**
 * What a member sees when the CG they typed is not a real, active one.
 *
 * Matches the API's own message for the same rejection (CG_DOES_NOT_EXIST in
 * course-access.service.ts) so the local check and the server answer read
 * identically — the member cannot tell, and should not have to, which of the two
 * turned them down.
 */
export const CG_DOES_NOT_EXIST = 'This CG does not exist. Enter an active Connect Group.';

export function hasLinkedCg(cgName) {
  if (typeof cgName !== 'string') return false;
  const normalized = cgName.trim().toLowerCase();
  if (!normalized) return false;
  return !UNSET_CG_NAMES.has(normalized);
}

/**
 * Normalises a CG name for directory matching: whitespace removed, lowercased.
 *
 * Apply it to BOTH sides. The directory (`GET /v2/profile/cg-validation`) is
 * keyed by the API's uppercased display names, spaces included ("TEENS 1"), and
 * since 2026-09-05 also by a whitespace-free alias — but older APIs publish only
 * the spaced form, and matching a stripped input against an unstripped key is
 * exactly what made every multi-word CG unresolvable here.
 */
export function normalizeCgName(cgName) {
  return String(cgName ?? '')
    .replace(/\s+/g, '')
    .toLowerCase();
}

/**
 * Whether a typed CG name matches the CG directory.
 *
 * A match is judged by the id it resolves to, not by the name: the directory
 * really does contain the id-0 "None" row. See below.
 *
 * `cgData` is the map from `GET /v2/profile/cg-validation` (CG name → id). When
 * it is empty — the fetch failed, and `registration/fetchCgData` swallows that
 * error — this returns true so the server stays the judge. Blocking on our own
 * outage would strand a member who typed a perfectly valid CG; `POST
 * /courses/link-cg` re-validates against the live directory regardless.
 */
export function isKnownCgName(cgName, cgData) {
  const normalized = normalizeCgName(cgName);
  if (!normalized) return false;

  if (!Object.keys(cgData || {}).length) return true;

  const id = resolveCgId(cgName, cgData);
  if (id === undefined) return false;

  // Matched — but the id decides. "None" IS a key here (the API puts every live
  // row in the map, and the row is real), so a name match alone would let the one
  // name meaning "I have no CG" satisfy the prompt that exists because they have
  // none. link-cg refuses the same id server-side.
  return id !== UNSET_CG_ID;
}

/**
 * The CG id a typed name resolves to in the directory, or undefined.
 *
 * Both the input and every key are passed through normalizeCgName, so
 * "teens1", "Teens 1" and " TEENS  1 " all find the "TEENS 1" entry (and a
 * "TEENS1" alias, if the API publishes one). This is the one place the app
 * turns a CG name into an id — registration, the profile CG-update card and
 * link-CG all go through it — so the key shape is a fact about the API, not
 * something each screen has to get right.
 *
 * Returns whatever id the directory holds, including 0 for the "None" row:
 * callers that must refuse the no-CG placeholder compare against UNSET_CG_ID.
 */
export function resolveCgId(cgName, cgData) {
  const normalized = normalizeCgName(cgName);
  if (!normalized || !cgData) return undefined;
  const match = Object.keys(cgData).find(key => normalizeCgName(key) === normalized);
  return match === undefined ? undefined : cgData[match];
}
